import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import ButtonType from "../components/ButtonType";
import CertBackfillImport from '../components/CertBackfillImport';
import DataSourceDropDown from '../components/DataSource/DropDown';
import EventLogDisplay from "../components/DataSource/EventLogDisplay";
import CircleX from '../components/Icons/CircleX';
import CircleCheck from '../components/Icons/CircleCheck';
import LoadingWrapper from "../components/LoadingWrapper";
import SelectField from "../components/SelectField";
import TextField from "../components/TextField";
import { errorToast, successToast } from "../components/Toasty";

import { GroupList } from '../enums/GroupList';

import { checkIfOneOffNCR, reliabilityEngineerAccess, developerAccess } from '../functions/AccessHelpers';
import JobCodeList from "../functions/JobCode";

import DataService from "../services/DataService";
import DataSourceService from "../services/DataSourceService";
import FormEntryService from "../services/FormEntryService";
import TaskService from '../services/TaskService';
import TestService from '../services/TestService';

import DataSourceResponse from '../types/Data/DataSourcesResponse';
import DataSourceHistoryDetail from "../types/DataSource/DataSourceHistoryDetail";
import EmployeeInfo from "../types/Employee/EmployeeInfo";
import EnvironmentData from "../types/FormsOnFire/EnvironmentData";
import ITestGroup from '../types/TestGroup';
import ProjectDropdown from '../components/ProjectDropdown';
import Project from '../types/Dashboard/Project';

type Props = {
    preloadedGroups?: ITestGroup[];
    updateTestGroups?: (e: any) => void;
    currentTestJobCode?: string;
    updateTestJobCode?: (jobCode: string) => void;
    updateUseTestUser?: () => void;
    currentJobCode?: string;
    usingTestUser?: boolean;
    userData?: EmployeeInfo;
    projects?: Project[];
    cookieName: string;
};

type Group = {
    value: string;
    label: string;
}

type ncrSubmission = {
    value: string;
    success: boolean;
    running: boolean;
}

const animatedComponents = makeAnimated();

const AdminPage = ({
    preloadedGroups,
    updateTestGroups,
    updateTestJobCode,
    updateUseTestUser,
    currentJobCode,
    usingTestUser,
    userData,
    projects,
    cookieName,
}: Props) => {
    const ncrListRef = useRef<string[]>([]);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const [selectedProject, setSelectedProject] = useState<string | undefined>(undefined);
    const [selectedOptionsGroup, setSelectedOptionsGroup] = useState<Group[]>([]);
    const [selectOptionsGroup, setSelectOptionsGroup] = useState<Group[]>([]);
    const [defaultGroupValues, setDefaultGroupValues] = useState<ITestGroup[]>(preloadedGroups ?? []);
    const [ncrSubmissionList, setNcrSubmissionList] = useState<ncrSubmission[]>([]);
    const [selectedJobCode, setSelectedJobCode] = useState(currentJobCode ?? '');
    const [headersLoading, setHeadersLoading] = useState(false);
    const [dataSourceHeaders, setDataSourceHeaders] = useState<DataSourceResponse | undefined>(undefined);
    const [selectedDataSource, setSelectedDataSource] = useState('');
    const [resettingCache, setResettingCache] = useState(false);
    const [resettingCacheError, setResettingCacheError] = useState(false);
    const [gettingEventLogs, setGettingEventLogs] = useState(false);
    const [gettingEventLogsError, setGettingEventLogsError] = useState(false);
    const [showEventLogModal, setShowEventLogModal] = useState(false);
    const [missingNCR, setMissingNCR] = useState({
        companyID: "",
        formEntryID: ""
    });
    const [missingNCRStatus, setMissingNCRStatus] = useState("");
    const [eventLogDetails, setEventLogDetails] = useState<DataSourceHistoryDetail[]>([]);

    const setAllGroups = useCallback(() => {
        const options = Object.entries(GroupList).map(value => ({
            "value": value[0],
            "label": value[1]
        }));
        setSelectOptionsGroup(options);
    }, []);

    const getGroupValues = useCallback(() => {
        const userEmail = userData?.email ?? "";
        TestService.getGroup(userEmail)
            .then((response) => {
                if (defaultGroupValues !== response.data) {
                    setDefaultGroupValues(response.data);

                    if (updateTestGroups) {
                        updateTestGroups(response.data);
                    }
                }
            }).catch((e) => {
                console.log(e);
            });
    }, [userData?.email, defaultGroupValues, updateTestGroups]);

    const sendGroupsToAPI = useCallback(async (selectedGroups: Group[], username: string) => {
        // creates a pipe separated group list and remove the last pipe.
        let groupString = "";
        selectedGroups.forEach(o => groupString = groupString + o.value + "|");
        groupString = groupString.slice(0, -1);

        // Create TestGroup object
        const data: ITestGroup = {
            id: 0,
            userEmail: username,
            group: groupString
        };

        TestService.insertGroup(data)
            .then(() => {
                // Refresh group list
                getGroupValues();
            }).catch((e) => {
                console.log(e);
            });
    }, [getGroupValues]);

    const getHeaders = useCallback(async () => {
        if (!selectedProject) {
            // No project selected yet, skip fetching headers
            return;
        }
        setHeadersLoading(true);
        try {
            const response = await DataService.getAllHeaders(selectedProject);
            if (!mountedRef.current) return;
            setDataSourceHeaders(response.data);
        } catch (error) {
            if (!mountedRef.current) return;
            console.log('Error occurred while getting the Data Source headers', error);
        } finally {
            if (mountedRef.current) setHeadersLoading(false);
        }
    }, [selectedProject]);

    useEffect(() => {
        setAllGroups();
        getHeaders();
    }, [setAllGroups, getHeaders]);

    const handleChangeSelectGroup = useCallback((selectedGroups: Group[]) => {
        setSelectedOptionsGroup(selectedGroups);
    }, []);

    const processNCR = useCallback(async (ncrString: string) => {
        if (!ncrListRef.current.includes(ncrString)) {
            console.log(`${ncrString} not found in NCR List`);
            return;
        }
        // get the list info right away
        const list = [...ncrSubmissionList];
        let submission = list?.find((n) => n.value === ncrString);
        // check to make sure it is non-reentrant
        // can leave out the success check if we want to be able to re-run the NCR
        if (submission?.success || submission?.running) {
            return;
        }

        // if the submission doesn't exist yet build it and push it.
        if (!submission) {
            submission = {
                value: ncrString,
                success: false,
                running: false
            };
            list.push(submission);
        }

        // get the index to make accessing the NCR Submission values easier
        const index = list.findIndex((n) => n.value === ncrString);

        list[index].running = true;
        setNcrSubmissionList([...list]);

        // -----  UNCOMMENT BELOW FOR INTEGRATION TESTING -----
        await TaskService.updateBmodsNcrById(ncrString)
            .then((response) => {
                if (response.data.includes('PASS')) {
                    list[index].success = true;
                }
            })
            .catch((response) => {
                console.log(response);
            });

        list[index].running = false;
        setNcrSubmissionList([...list]);
    }, [ncrSubmissionList]);

    const updateMissingNCRCompanyID = useCallback((event: any) => {
        setMissingNCR(prev => ({
            companyID: event.target.value,
            formEntryID: prev.formEntryID
        }));
    }, []);

    const updateMissingNCRFormEntryID = useCallback((event: any) => {
        setMissingNCR(prev => ({
            companyID: prev.companyID,
            formEntryID: event.target.value
        }));
    }, []);

    const submitMissingNCR = useCallback(async () => {
        setMissingNCRStatus("Loading...");

        // Get the form entry data
        await DataService.getFormEntry(missingNCR.companyID, missingNCR.formEntryID)
            .then((response) => {
                // Check for error while getting form entry (return if error)
                if (response.status >= 300) {
                    setMissingNCRStatus(`Error occurred getting the form entry from FOF API (HTTP status code: ${response.status})`);
                    return;
                }

                // Invalid company ID
                if (response.data.integrationKey === null) {
                    setMissingNCRStatus("Invalid 'Company ID' value");
                }
                // Invalid form entry ID
                else if (response.data.entry === null) {
                    setMissingNCRStatus("Invalid 'Form Entry ID' value");
                }
                // Valid input
                else {
                    // Submit form entry
                    FormEntryService.submit(response.data)
                        .then((result) => {
                            // Check for error while submitting form entry (return if error)
                            if (result.status >= 300) {
                                setMissingNCRStatus(`Error occurred submitting the form entry to Blattner Portal (HTTP status code: ${result.status})`);
                                return;
                            }

                            // Check if already exists
                            if (result.data.includes("already exists")) {
                                setMissingNCRStatus("This form entry already exists");
                            }
                            // Success
                            else {
                                setMissingNCRStatus("Success!");
                            }
                        });
                }
            });
    }, [missingNCR.companyID, missingNCR.formEntryID]);

    const ncrActionList = useCallback(() => {
        const showNcrList = () =>
            ncrListRef.current.map((ncr) => {
                const ncrSubmission = ncrSubmissionList?.find((n) => n.value === ncr);
                return (
                    <div key={ncr}>
                        <div className='row py-1'>
                            <div className='col-2 mt-2 border-bottom'>
                                {ncr}
                            </div>
                            <div className='col-1 border-bottom py-1'>
                                <Button type="button" size="sm" variant="outline-secondary" onClick={() => { processNCR(ncr) }}>
                                    {(!ncrSubmission?.running && (!ncrSubmission || !ncrSubmission.success)) && <>run</>}
                                    {(!ncrSubmission?.running && (ncrSubmission?.success)) && <>done</>}
                                    {(ncrSubmission?.running) && <>running</>}
                                </Button>
                            </div>
                            {(ncrSubmission) && <div className='col-1 border-bottom mt-1 py-1'>
                                {(!ncrSubmission.success) && <CircleX />}
                                {(ncrSubmission.success) && <CircleCheck />}
                            </div>}
                        </div>
                    </div>
                );
            });
        return (
            <>
                {showNcrList()}
            </>
        );
    }, [ncrSubmissionList, processNCR]);

    const allowNcrAccess = useCallback(() => {
        if (!userData) {
            return false;
        }
        return checkIfOneOffNCR(userData);
    }, [userData]);

    const allowSpecialAccess = useCallback(() => {
        if (!userData) {
            return false;
        }
        return (reliabilityEngineerAccess(userData.jobCode)
            || developerAccess(userData.jobCode));
    }, [userData]);

    const adGroupSelection = useCallback(() => {
        return (
            <div
                className='row' style={{ height: '70vh' }}
            >
                <div className='col'>
                    <div
                        className='row h5'
                        style={{ height: '3vh' }}
                    >
                        You are a part of these groups:
                    </div>
                    <div
                        className='row mt-3 overflow-auto border border-light rounded shadow-sm'
                        style={{ height: '50vh' }}
                    >
                        {(defaultGroupValues.length > 0) ? (
                            <div>
                                {defaultGroupValues.map((val) => (
                                    <div
                                        key={`${val.id}`}
                                        className='border border-light rounded px-2 py-1'
                                    >
                                        {val.group}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className=''>
                                No Groups Selected
                            </div>
                        )}
                    </div>
                    <div className='row h5 mt-3'
                        style={{ height: '15vh' }}
                    >
                        <div className='row'>
                            Select new groups to change:
                        </div>
                        <div className='row mt-2'>
                            <Select
                                closeMenuOnSelect={false}
                                components={animatedComponents}
                                isMulti={true}
                                onChange={(option: any) => handleChangeSelectGroup(option)}
                                options={selectOptionsGroup}
                            />
                        </div>
                        <div className='row text-center'>
                            <div className='col w-25 mx-1 my-1'>
                                <Button
                                    type="button"
                                    className='btn btn-sm'
                                    onClick={() => {
                                        sendGroupsToAPI(selectedOptionsGroup, userData?.email ?? "")
                                    }}>
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [defaultGroupValues, selectOptionsGroup, selectedOptionsGroup, handleChangeSelectGroup, sendGroupsToAPI, userData?.email]);

    const jobSelector = useCallback(() => {
        const updateJobCode = (e: any) => {
            const newSelectedJobCode = e.target.value;
            if (updateTestJobCode) {
                updateTestJobCode(newSelectedJobCode);
            }
            setSelectedJobCode(newSelectedJobCode);
        };
        return (
            <div
                className='row' style={{ height: '70vh' }}
            >
                <div>
                    <SelectField
                        dataName="JobCodeSelect"
                        title="Job Code"
                        options={JobCodeList.map((jobCode) => ({ label: `${jobCode.name} - ${jobCode.code}`, value: jobCode.code }))}
                        value={selectedJobCode}
                        onchange={updateJobCode}
                    />
                </div>
            </div>
        );
    }, [selectedJobCode, updateTestJobCode]);

    const handleDataSourceChange = useCallback((value: string | undefined) => {
        if (!value) return;
        setSelectedDataSource(value);
    }, []);

    const selectDataSourceHeaders = useCallback((headers: DataSourceResponse) => {
        if (!headers) {
            return;
        }
        return (
            <DataSourceDropDown
                dataSourceList={headers.dataSources}
                selectionHandler={handleDataSourceChange}
                cookiesName='dataSource'
            />
        );
    }, [handleDataSourceChange]);

    const resetCacheClick = useCallback(async () => {
        if (!selectedDataSource || !selectedProject) {
            return;
        }
        setResettingCache(true);
        setResettingCacheError(false);
        try {
            const { status, statusText } = await DataService.clearDataSourceCache(selectedProject, selectedDataSource);

            if (status !== 200) {
                throw new Error(`Error resetting cache with ${status}: ${statusText}`);
            }
            const msg = (
                <div>
                    {`Cleared Cache for ${selectedDataSource} data source in environment ${selectedProject}`}
                </div>
            );
            successToast(msg);
        } catch (error) {
            setResettingCacheError(true);
            const msg = (
                <div>
                    {`Error Clearing Cache for ${selectedProject}, ${selectedDataSource}: "${error}"`}
                </div>
            );
            errorToast(msg);
        } finally {
            setResettingCache(false);
        }
    }, [selectedDataSource, selectedProject]);

    const getDataSourceHistoryDetails = useCallback(async () => {
        // prevent re-entrance
        if (gettingEventLogs) {
            return;
        }
        // make sure we have all the info we need to make the call to get event logs
        if (!selectedDataSource || !selectedProject) {
            return;
        }

        setGettingEventLogs(true);
        setGettingEventLogsError(false);
        let hasError = false;
        try {
            const { status, statusText, data } = await DataSourceService.getDataSourceHistory(selectedProject, selectedDataSource);
            if (status !== 200) {
                throw new Error(`Error getting event logs with ${status}: ${statusText}`);
            }

            setEventLogDetails(data.sort((a, b) => b.date - a.date));

        } catch (error) {
            setGettingEventLogsError(true);
            hasError = true;
            const msg = (
                <div>
                    {`Error getting Event Log for ${selectedDataSource} in ${selectedProject}`}
                </div>
            );
            errorToast(msg);
        } finally {
            setGettingEventLogs(false);
            if (!hasError) {
                setShowEventLogModal(true);
            }
        }
    }, [gettingEventLogs, selectedDataSource, selectedProject]);

    const DSManagment = useCallback(() => {
        const handleButtonColor = () => {
            if (resettingCacheError) {
                return 'btn-danger';
            }
            return 'btn-primary';
        };

        // we are not using the loading wrapper for the buttons because we want to use a
        //   different color for the spinner
        return (
            <div className='col'>
                <div className='row'>
                    <ProjectDropdown
                        projects={projects}
                        cookieName={cookieName}
                        onProjectChange={setSelectedProject}
                    />
                </div>
                <div className='row'>
                    <LoadingWrapper showLoading={headersLoading}>
                        {dataSourceHeaders && selectDataSourceHeaders(dataSourceHeaders)}
                    </LoadingWrapper>
                </div>
                <div className='row'>
                    <div className='col'>
                        <div>
                            <b>Reset Cache</b>
                        </div>
                        <button
                            type='button'
                            className={`btn ${handleButtonColor()} admin-page-ds-button-width`}
                            title='reset cache for given project and data source'
                            onClick={() => { resetCacheClick() }}
                            disabled={selectedDataSource === ''}
                        >
                            {resettingCache ? <Spinner animation='border' size='sm' role='status' aria-hidden='true' /> : <>Reset Cache</>}
                        </button>
                    </div>
                    <div className='col'>
                        <div>
                            <b>Event Log</b>
                        </div>
                        <button
                            type='button'
                            className={`btn ${handleButtonColor()} admin-page-ds-button-width`}
                            title='view event log for the selected data source and project'
                            onClick={() => { getDataSourceHistoryDetails() }}
                            disabled={selectedDataSource === ''}
                        >
                            {gettingEventLogs ? <Spinner animation='border' size='sm' role='status' aria-hidden='true' /> : <>Get Logs</>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }, [headersLoading, dataSourceHeaders, selectDataSourceHeaders, resettingCacheError, resettingCache, selectedDataSource, resetCacheClick, gettingEventLogs, getDataSourceHistoryDetails]);

    const EventLogModal = useCallback(() => {
        const projectNameDisplay = selectedProject;
        const projectNumberDisplay = selectedProject;
        const dataSourceNameDisplay = dataSourceHeaders?.dataSources.find(x => x.externalId === selectedDataSource)?.name;

        return (
            <Modal
                className='modal-xl'
                show={showEventLogModal}
                onHide={() => setShowEventLogModal(false)}
            >
                <Modal.Header
                    closeButton
                >
                    <div className='row' style={{ width: '100%' }}>
                        <div className='col-1'></div>
                        <div className='col-10 text-center'>
                            <h3>
                                {projectNameDisplay}&nbsp;({projectNumberDisplay})
                                <br />{dataSourceNameDisplay}
                                <br />Event Logs
                            </h3>
                        </div>
                        <div className='col-1'></div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className='overflow-auto' style={{ maxHeight: '75vh' }}>
                        <EventLogDisplay eventLogs={eventLogDetails} />
                    </div>
                </Modal.Body>
            </Modal>
        );
    }, [selectedProject, dataSourceHeaders, selectedDataSource, showEventLogModal, eventLogDetails]);

    return (
        <div className='row w-75' style={{ height: '85vh' }}>
            {EventLogModal()}
            <div>
                <div className='col'>
                    <div className='row' style={{ height: '10vh' }}>
                        <div className='col-3'>
                        </div>
                        <div className='col-6 text-center h1 my-3'>
                            Admin Page
                        </div>
                        <div className='col-3 my-3'>
                        </div>
                    </div>
                    <div className='row' style={{ height: '2vh' }}>
                        <div className="square"></div>
                    </div>
                    {(allowSpecialAccess()) &&
                        <div className='row mb-3'>
                            <div className='col border-end'>
                                {DSManagment()}
                            </div>
                            <div className='col border-end'>
                                {(allowNcrAccess()) &&
                                    <>
                                        {ncrActionList()}
                                        <h4><b><u>Submit Missing NCR</u></b></h4>
                                        <TextField
                                            dataName={"formEntryID"}
                                            title={"Company ID"}
                                            value={missingNCR.companyID}
                                            onchange={updateMissingNCRCompanyID}
                                        />
                                        <TextField
                                            dataName={"formEntryID"}
                                            title={"Form Entry ID"}
                                            value={missingNCR.formEntryID}
                                            onchange={updateMissingNCRFormEntryID}
                                        />
                                        <ButtonType
                                            dataName={"submitNCR"}
                                            buttonText={"Submit"}
                                            eventHandler={submitMissingNCR}
                                        />
                                        <br />
                                        <p>{missingNCRStatus}</p>
                                    </>
                                }
                            </div>
                            <div className='col text-center'>
                            </div>
                        </div>
                    }
                    {(allowSpecialAccess()) &&
                        <div className='row' style={{ height: '2vh' }}>
                            <div className="square"></div>
                        </div>
                    }
                    {(allowSpecialAccess()) &&
                        <div className='row mb-3'>
                            <div className='col'>
                                <CertBackfillImport userEmail={userData?.email ?? ''} />
                            </div>
                        </div>
                    }
                    <div className='row' style={{ height: '70vh' }}>
                        <div className='row'>
                            <div className='col-9'>
                            </div>
                            <div className='col-3'>
                                <div
                                    className='form-check form-switch'
                                    title='toggles whether or not to use the test user'
                                >
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        role='switch'
                                        onChange={() => {
                                            if (updateUseTestUser) {
                                                updateUseTestUser();
                                            }
                                        }}
                                        checked={usingTestUser}
                                    />
                                    <label className='form-check-label'>Test User</label>
                                </div>
                            </div>
                        </div>
                        <div
                            className='col'
                        >
                            {adGroupSelection()}
                        </div>
                        <div className='col'>
                            {jobSelector()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPage;